import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Card from "components/card";
import InputField from "components/fields/InputField";
import { Toaster } from "react-hot-toast";

const CalibrateAnalyzed = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id, type } = location.state || {};

  const [formData, setFormData] = useState({
    weighing_bridge_weight: "",
    number_of_bags: "",
    price_per_kg: "",
    entry_moisture: "",
    calibration_moisture: "",
    dust_offloaded: "",
    dust_after_drying: "",
    actual_calibrated_bags: "",
    actual_calibrated_kg: "",
    // moisture_to_autoclave: "", // Commented out
    size_17_weight: "",
    size_17_stacking_zone: "",
    size_19_weight: "",
    size_19_stacking_zone: "",
    size_21_weight: "",
    size_21_stacking_zone: "",
    size_23_weight: "",
    size_23_stacking_zone: "",
    size_25_weight: "",
    size_25_stacking_zone: "",
    size_28_weight: "",
    size_28_stacking_zone: "",
    total_weight: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [errors, setErrors] = useState({});

  // Add this function to generate stacking zone options
  const stackingZoneOptions = [
    ...Array.from({ length: 9 }, (_, i) => `R${i + 1}`),
    ...Array.from({ length: 9 }, (_, i) => `L${i + 1}`)
  ];

  useEffect(() => {
    if (!id || !type) {
      navigate('/admin/warehouse/view-analyzed');
      return;
    }

    // Commented out data fetching
    /*
    const fetchData = async () => {
      try {
        const response = await fetch(`https://usibras-web-backend.onrender.com/api/analyzed/${id}`);
        const data = await response.json();
        if (response.ok) {
          setFormData(data);
        } else {
          showAlert('Failed to fetch data', 'error');
        }
      } catch (error) {
        showAlert('Error fetching data', 'error');
      }
    };

    fetchData();
    */
  }, [id, type, navigate]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: '', type: '' }), 3000);
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Initial Measurements
    if (!formData.weighing_bridge_weight) newErrors.weighing_bridge_weight = 'Weighing bridge weight is required';
    if (!formData.number_of_bags) newErrors.number_of_bags = 'Number of bags is required';
    // Remove price_per_kg validation since it's optional
    // if (!formData.price_per_kg) newErrors.price_per_kg = 'Price per kg is required';
    // Remove entry_moisture validation since the field is commented out
    // if (!formData.entry_moisture) newErrors.entry_moisture = 'Entry moisture is required';

    // Dust Measurements
    if (!formData.dust_offloaded) newErrors.dust_offloaded = 'Dust offloaded is required';
    if (!formData.dust_after_drying) newErrors.dust_after_drying = 'Dust after drying is required';

    // Calibration Data
    if (!formData.calibration_moisture) newErrors.calibration_moisture = 'Calibration moisture is required';
    if (!formData.actual_calibrated_bags) newErrors.actual_calibrated_bags = 'Actual calibrated bags is required';
    // Remove actual_calibrated_kg validation since it's optional
    // if (!formData.actual_calibrated_kg) newErrors.actual_calibrated_kg = 'Actual calibrated kg is required';
    // if (!formData.moisture_to_autoclave) newErrors.moisture_to_autoclave = 'Moisture to autoclave is required';

    // Size Distribution
    const sizes = ['17', '19', '21', '23', '25', '28'];
    sizes.forEach(size => {
      if (!formData[`size_${size}_weight`]) newErrors[`size_${size}_weight`] = `Size ${size} weight is required`;
      if (!formData[`size_${size}_stacking_zone`]) newErrors[`size_${size}_stacking_zone`] = `Size ${size} stacking zone is required`;
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setShowVerificationModal(true);
    } else {
      showAlert('Please fill in all required fields', 'error');
    }
  };

  const handleVerifiedSubmit = async () => {
    setIsLoading(true);
    setShowVerificationModal(false);
    
    try {
      // Transform the data to match the API parameter format
      const transformedData = {
        id: id,
        weighing_bridge_weight: parseFloat(formData.weighing_bridge_weight),
        number_of_bags: parseInt(formData.number_of_bags),
        price_per_kg: formData.price_per_kg ? parseFloat(formData.price_per_kg) : null,
        entry_moisture: parseFloat(formData.entry_moisture),
        calibration_moisture: parseFloat(formData.calibration_moisture),
        dust_offloaded: parseFloat(formData.dust_offloaded),
        dust_after_drying: parseFloat(formData.dust_after_drying),
        actual_calibrated_bags: parseInt(formData.actual_calibrated_bags),
        actual_calibrated_kg: formData.actual_calibrated_kg ? parseFloat(formData.actual_calibrated_kg) : null,
        // moisture_to_autoclave: parseFloat(formData.moisture_to_autoclave),
        size_17_weight: parseFloat(formData.size_17_weight),
        size_17_stacking_zone: formData.size_17_stacking_zone,
        size_19_weight: parseFloat(formData.size_19_weight),
        size_19_stacking_zone: formData.size_19_stacking_zone,
        size_21_weight: parseFloat(formData.size_21_weight),
        size_21_stacking_zone: formData.size_21_stacking_zone,
        size_23_weight: parseFloat(formData.size_23_weight),
        size_23_stacking_zone: formData.size_23_stacking_zone,
        size_25_weight: parseFloat(formData.size_25_weight),
        size_25_stacking_zone: formData.size_25_stacking_zone,
        size_28_weight: parseFloat(formData.size_28_weight),
        size_28_stacking_zone: formData.size_28_stacking_zone,
        total_weight: parseFloat(formData.total_weight)
      };

      console.log('Sending data:', transformedData);

      const response = await fetch("https://usibras-web-backend.onrender.com/api/calibrateRCN", {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(transformedData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Something went wrong');
      }
      
      showAlert(`Successfully calibrated! ID: ${data.id}`, 'success');
      navigate('/admin/warehouse/view-analyzed');
      
      setFormData({
        weighing_bridge_weight: "",
        number_of_bags: "",
        price_per_kg: "",
        entry_moisture: "",
        calibration_moisture: "",
        dust_offloaded: "",
        dust_after_drying: "",
        actual_calibrated_bags: "",
        actual_calibrated_kg: "",
        size_17_weight: "",
        size_17_stacking_zone: "",
        size_19_weight: "",
        size_19_stacking_zone: "",
        size_21_weight: "",
        size_21_stacking_zone: "",
        size_23_weight: "",
        size_23_stacking_zone: "",
        size_25_weight: "",
        size_25_stacking_zone: "",
        size_28_weight: "",
        size_28_stacking_zone: "",
        total_weight: "",
      });
    } catch (error) {
      console.error('Error details:', error);
      showAlert(error.message || "An error occurred", 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const calculateTotal = (data) => {
    const sizes = ['size_17_weight', 'size_19_weight', 'size_21_weight', 'size_23_weight', 'size_25_weight', 'size_28_weight'];
    const total = sizes.reduce((sum, size) => {
      const value = parseFloat(data[size]) || 0;
      return sum + value;
    }, 0);
    return total.toFixed(2);
  };

  const handleSizeChange = (size, value) => {
    setFormData(prev => ({
      ...prev,
      [`size_${size}_weight`]: value,
      total_weight: calculateTotal({
        ...prev,
        [`size_${size}_weight`]: value
      })
    }));
  };

  const handleStackingZoneChange = (size, value) => {
    setFormData(prev => ({
      ...prev,
      [`size_${size}_stacking_zone`]: value
    }));
  };

  return (
    <div className="mt-3">
      <Toaster position="top-right" />
      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white p-8 rounded-xl shadow-xl flex flex-col items-center">
            <div className="w-12 h-12 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-navy-700 font-medium">Processing...</p>
          </div>
        </div>
      )}

      {alert.show && (
        <div className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg ${
          alert.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white z-50`}>
          {alert.message}
        </div>
      )}

      <Card extra={"w-full p-4"}>
        <div className="mb-8 w-full">
          <h4 className="text-xl font-bold text-navy-700 dark:text-white">
            Calibrate {type} RCN - ID: {id}
          </h4>
          <p className="mt-2 text-base text-gray-600">
            Please fill in all the required information for calibration
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="mb-6">
            <h5 className="mb-4 text-lg font-medium text-navy-700 dark:text-white">
              Initial Measurements
            </h5>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <InputField
                label="Weighing Bridge Weight*"
                id="weighing_bridge_weight"
                type="number"
                value={formData.weighing_bridge_weight}
                onChange={handleChange}
                error={errors.weighing_bridge_weight}
                required
              />
              <InputField
                label="Number of Bags*"
                id="number_of_bags"
                type="number"
                value={formData.number_of_bags}
                onChange={handleChange}
                error={errors.number_of_bags}
                required
              />
              <InputField
                label="Price per Kg"
                id="price_per_kg"
                type="number"
                value={formData.price_per_kg}
                onChange={handleChange}
                error={errors.price_per_kg}
              />
              {/* Entry Moisture - Commented out
              <InputField
                label="Entry Moisture*"
                id="entry_moisture"
                type="number"
                value={formData.entry_moisture}
                onChange={handleChange}
                error={errors.entry_moisture}
                required
              />
              */}
            </div>
          </div>

          {/* Dust Measurements */}
          <div className="mb-6">
            <h5 className="mb-4 text-lg font-medium text-navy-700 dark:text-white">
              Dust Measurements
            </h5>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <InputField
                label="Dust Offloaded*"
                id="dust_offloaded"
                type="number"
                step="0.01"
                placeholder="Enter dust offloaded"
                value={formData.dust_offloaded}
                onChange={handleChange}
                required
                error={errors.dust_offloaded}
              />
              <InputField
                label="Dust After Drying*"
                id="dust_after_drying"
                type="number"
                step="0.01"
                placeholder="Enter dust after drying"
                value={formData.dust_after_drying}
                onChange={handleChange}
                required
                error={errors.dust_after_drying}
              />
            </div>
          </div>

          {/* Calibration Data */}
          <div className="mb-6">
            <h5 className="mb-4 text-lg font-medium text-navy-700 dark:text-white">
              Calibration Data
            </h5>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <InputField
                label="Calibration Moisture %*"
                id="calibration_moisture"
                type="number"
                step="0.01"
                placeholder="Enter calibration moisture"
                value={formData.calibration_moisture}
                onChange={handleChange}
                required
                error={errors.calibration_moisture}
              />
              <InputField
                label="Actual Calibrated Bags*"
                id="actual_calibrated_bags"
                type="number"
                placeholder="Enter actual calibrated bags"
                value={formData.actual_calibrated_bags}
                onChange={handleChange}
                required
                error={errors.actual_calibrated_bags}
              />
              <InputField
                label="Actual Calibrated KG"
                id="actual_calibrated_kg"
                type="number"
                step="0.01"
                placeholder="Enter actual calibrated KG"
                value={formData.actual_calibrated_kg}
                onChange={handleChange}
                error={errors.actual_calibrated_kg}
              />
              {/* Moisture to Autoclave - Commented out
              <InputField
                label="Moisture to Autoclave (%)"
                type="number"
                value={formData.moisture_to_autoclave}
                onChange={(e) => handleChange('moisture_to_autoclave', e.target.value)}
                min="0"
                max="100"
                step="0.01"
                placeholder="Enter moisture percentage"
                error={errors.moisture_to_autoclave}
              />
              */}
            </div>
          </div>

          {/* Updated Size Distribution Section */}
          <div className="mt-6">
            <h4 className="mb-4 text-xl font-bold text-navy-700 dark:text-white">
              Size Distribution (KG)
            </h4>
            <div className="grid grid-cols-1 gap-6">
              {/* Size 17 */}
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="text-lg font-medium text-navy-700">Size 17*</h5>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    label="Weight (KG)*"
                    type="number"
                    value={formData.size_17_weight}
                    onChange={(e) => handleSizeChange('17', e.target.value)}
                    min="0"
                    step="0.01"
                    placeholder="Enter weight"
                    required
                    error={errors.size_17_weight}
                  />
                  <div className="flex flex-col">
                    <label className="text-sm text-navy-700 font-medium mb-1">Stacking Zone*</label>
                    <select
                      className="border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-500"
                      value={formData.size_17_stacking_zone}
                      onChange={(e) => handleStackingZoneChange('17', e.target.value)}
                      required
                    >
                      <option value="">Select stacking zone</option>
                      {stackingZoneOptions.map(zone => (
                        <option key={`17-${zone}`} value={zone}>{zone}</option>
                      ))}
                    </select>
                    {errors.size_17_stacking_zone && (
                      <p className="text-red-500 text-xs mt-1">{errors.size_17_stacking_zone}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Size 19 */}
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="text-lg font-medium text-navy-700">Size 19*</h5>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    label="Weight (KG)*"
                    type="number"
                    value={formData.size_19_weight}
                    onChange={(e) => handleSizeChange('19', e.target.value)}
                    min="0"
                    step="0.01"
                    placeholder="Enter weight"
                    required
                    error={errors.size_19_weight}
                  />
                  <div className="flex flex-col">
                    <label className="text-sm text-navy-700 font-medium mb-1">Stacking Zone*</label>
                    <select
                      className="border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-500"
                      value={formData.size_19_stacking_zone}
                      onChange={(e) => handleStackingZoneChange('19', e.target.value)}
                      required
                    >
                      <option value="">Select stacking zone</option>
                      {stackingZoneOptions.map(zone => (
                        <option key={`19-${zone}`} value={zone}>{zone}</option>
                      ))}
                    </select>
                    {errors.size_19_stacking_zone && (
                      <p className="text-red-500 text-xs mt-1">{errors.size_19_stacking_zone}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Size 21 */}
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="text-lg font-medium text-navy-700">Size 21*</h5>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    label="Weight (KG)*"
                    type="number"
                    value={formData.size_21_weight}
                    onChange={(e) => handleSizeChange('21', e.target.value)}
                    min="0"
                    step="0.01"
                    placeholder="Enter weight"
                    required
                    error={errors.size_21_weight}
                  />
                  <div className="flex flex-col">
                    <label className="text-sm text-navy-700 font-medium mb-1">Stacking Zone*</label>
                    <select
                      className="border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-500"
                      value={formData.size_21_stacking_zone}
                      onChange={(e) => handleStackingZoneChange('21', e.target.value)}
                      required
                    >
                      <option value="">Select stacking zone</option>
                      {stackingZoneOptions.map(zone => (
                        <option key={`21-${zone}`} value={zone}>{zone}</option>
                      ))}
                    </select>
                    {errors.size_21_stacking_zone && (
                      <p className="text-red-500 text-xs mt-1">{errors.size_21_stacking_zone}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Size 23 */}
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="text-lg font-medium text-navy-700">Size 23*</h5>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    label="Weight (KG)*"
                    type="number"
                    value={formData.size_23_weight}
                    onChange={(e) => handleSizeChange('23', e.target.value)}
                    min="0"
                    step="0.01"
                    placeholder="Enter weight"
                    required
                    error={errors.size_23_weight}
                  />
                  <div className="flex flex-col">
                    <label className="text-sm text-navy-700 font-medium mb-1">Stacking Zone*</label>
                    <select
                      className="border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-500"
                      value={formData.size_23_stacking_zone}
                      onChange={(e) => handleStackingZoneChange('23', e.target.value)}
                      required
                    >
                      <option value="">Select stacking zone</option>
                      {stackingZoneOptions.map(zone => (
                        <option key={`23-${zone}`} value={zone}>{zone}</option>
                      ))}
                    </select>
                    {errors.size_23_stacking_zone && (
                      <p className="text-red-500 text-xs mt-1">{errors.size_23_stacking_zone}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Size 25 */}
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="text-lg font-medium text-navy-700">Size 25*</h5>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    label="Weight (KG)*"
                    type="number"
                    value={formData.size_25_weight}
                    onChange={(e) => handleSizeChange('25', e.target.value)}
                    min="0"
                    step="0.01"
                    placeholder="Enter weight"
                    required
                    error={errors.size_25_weight}
                  />
                  <div className="flex flex-col">
                    <label className="text-sm text-navy-700 font-medium mb-1">Stacking Zone*</label>
                    <select
                      className="border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-500"
                      value={formData.size_25_stacking_zone}
                      onChange={(e) => handleStackingZoneChange('25', e.target.value)}
                      required
                    >
                      <option value="">Select stacking zone</option>
                      {stackingZoneOptions.map(zone => (
                        <option key={`25-${zone}`} value={zone}>{zone}</option>
                      ))}
                    </select>
                    {errors.size_25_stacking_zone && (
                      <p className="text-red-500 text-xs mt-1">{errors.size_25_stacking_zone}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Size 28 */}
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="text-lg font-medium text-navy-700">Size 28*</h5>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    label="Weight (KG)*"
                    type="number"
                    value={formData.size_28_weight}
                    onChange={(e) => handleSizeChange('28', e.target.value)}
                    min="0"
                    step="0.01"
                    placeholder="Enter weight"
                    required
                    error={errors.size_28_weight}
                  />
                  <div className="flex flex-col">
                    <label className="text-sm text-navy-700 font-medium mb-1">Stacking Zone*</label>
                    <select
                      className="border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-500"
                      value={formData.size_28_stacking_zone}
                      onChange={(e) => handleStackingZoneChange('28', e.target.value)}
                      required
                    >
                      <option value="">Select stacking zone</option>
                      {stackingZoneOptions.map(zone => (
                        <option key={`28-${zone}`} value={zone}>{zone}</option>
                      ))}
                    </select>
                    {errors.size_28_stacking_zone && (
                      <p className="text-red-500 text-xs mt-1">{errors.size_28_stacking_zone}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Total */}
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                  <h5 className="text-lg font-medium text-navy-700">Total Weight</h5>
                  <span className="text-xl font-bold text-brand-500">
                    {formData.total_weight} KG
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => setFormData({
                weighing_bridge_weight: "",
                number_of_bags: "",
                price_per_kg: "",
                entry_moisture: "",
                calibration_moisture: "",
                dust_offloaded: "",
                dust_after_drying: "",
                actual_calibrated_bags: "",
                actual_calibrated_kg: "",
                size_17_weight: "",
                size_17_stacking_zone: "",
                size_19_weight: "",
                size_19_stacking_zone: "",
                size_21_weight: "",
                size_21_stacking_zone: "",
                size_23_weight: "",
                size_23_stacking_zone: "",
                size_25_weight: "",
                size_25_stacking_zone: "",
                size_28_weight: "",
                size_28_stacking_zone: "",
                total_weight: "",
              })}
              className="rounded-xl bg-gray-500 px-5 py-3 text-base font-medium text-white transition duration-200 hover:bg-gray-600 active:bg-gray-700 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-800 dark:active:bg-navy-900"
              disabled={isLoading}
            >
              Clear
            </button>
            <button
              type="submit"
              className="rounded-xl bg-brand-500 px-5 py-3 text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-800 dark:active:bg-navy-900 flex items-center justify-center min-w-[120px]"
              disabled={isLoading}
            >
              Submit
            </button>
          </div>
        </form>
      </Card>

      {/* Add Verification Modal */}
      {showVerificationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white p-8 rounded-xl shadow-xl w-[800px] max-h-[80vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-navy-700 mb-6">
              Verify Calibration Data
            </h3>
            
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-semibold text-navy-700 mb-3">Basic Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Date</p>
                    <p className="font-medium">{formData.date}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Truck Number</p>
                    <p className="font-medium">{formData.truckNumber}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Origin</p>
                    <p className="font-medium">{formData.origin}</p>
                  </div>
                </div>
              </div>

              {/* Initial Measurements */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-semibold text-navy-700 mb-3">Initial Measurements</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Weighing Bridge Weight</p>
                    <p className="font-medium">{formData.weighing_bridge_weight} KG</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Number of Bags</p>
                    <p className="font-medium">{formData.number_of_bags}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Price per Kg</p>
                    <p className="font-medium">₵{formData.price_per_kg}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Entry Moisture</p>
                    <p className="font-medium">{formData.entry_moisture}%</p>
                  </div>
                </div>
              </div>

              {/* Size Distribution */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-semibold text-navy-700 mb-3">Size Distribution</h4>
                <div className="grid grid-cols-2 gap-4">
                  {['17', '19', '21', '23', '25', '28'].map((size) => (
                    <div key={size} className="bg-white p-3 rounded-lg border border-gray-200">
                      <h5 className="font-medium text-navy-700">Size {size}</h5>
                      <div className="mt-2">
                        <p className="text-sm text-gray-600">Weight</p>
                        <p className="font-medium">{formData[`size_${size}_weight`]} KG</p>
                      </div>
                      <div className="mt-2">
                        <p className="text-sm text-gray-600">Stacking Zone</p>
                        <p className="font-medium">{formData[`size_${size}_stacking_zone`]}</p>
                      </div>
                    </div>
                  ))}
                  <div className="col-span-2 bg-white p-3 rounded-lg border border-gray-200">
                    <h5 className="font-medium text-navy-700">Total Weight</h5>
                    <p className="text-xl font-bold text-brand-500">{formData.total_weight} KG</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-4 mt-8">
              <button
                type="button"
                onClick={() => setShowVerificationModal(false)}
                className="rounded-xl bg-gray-500 px-5 py-3 text-base font-medium text-white transition duration-200 hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleVerifiedSubmit}
                className="rounded-xl bg-brand-500 px-5 py-3 text-base font-medium text-white transition duration-200 hover:bg-brand-600"
              >
                Confirm & Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalibrateAnalyzed;
